import type React from "react";
import { theme, card, type as text, pageHeading, pageSubtitle } from "../../theme";
import { frameworkColor } from "../../nav/frameworkMeta";

// The one header every standard page opens with. The rest of a standard page is built from the
// same parts: StatusSummary, ControlFilterBar, ControlGroup, ControlRow and ControlDetail
// (components/standards/*), usually through components/FrameworkControlList.tsx.
//
//   eyebrow   the sub-page, named exactly as the sidebar names it ("Annex A controls",
//             "What applies (SoA)"), beside the standard's colour swatch
//   heading   the standard's name and edition ("ISO/IEC 27001:2022")
//   subtitle  one sentence on what this page is for
//   aside     page-level controls that belong to the whole page (PCI DSS SAQ selection)

/** The standard's own name and edition, as its publisher writes it. */
export const STANDARD_TITLES: Record<string, string> = {
  iso27001: "ISO/IEC 27001:2022",
  iso9001: "ISO 9001:2015",
  iso42001: "ISO/IEC 42001:2023",
  soc2: "SOC 2 Trust Services Criteria",
  pci_dss: "PCI DSS v4.0",
  cyber_essentials: "Cyber Essentials",
  cyber_essentials_plus: "Cyber Essentials Plus",
  iasme_ca_sole_trader: "IASME Cyber Assurance v7.0",
  iasme_ca_micro: "IASME Cyber Assurance v7.0",
  iasme_ca_small: "IASME Cyber Assurance v7.0",
  iasme_ca_medium_large: "IASME Cyber Assurance v7.0",
  nist_csf: "NIST Cybersecurity Framework 2.0",
  ncsc_caf: "NCSC Cyber Assessment Framework v4.0",
  iso45001: "ISO 45001:2018",
  iso14001: "ISO 14001:2015",
  iwa48: "IWA 48:2024",
};

/**
 * Sub-page names, matching the sidebar. The sidebar shows "What applies (SoA)" for the
 * Statement of Applicability (clientNav PLAIN_PAGE_LABELS); the page says the same.
 */
export const STANDARD_PAGE_LABELS = {
  soa: "What applies (SoA)",
  clauses: "Clauses",
  requiredClauses: "Required clauses",
  annexA: "Annex A controls",
  annexB: "Annex B controls",
  guidelines: "Guidelines",
  criteria: "Trust services criteria",
  assessment: "Assessment",
  referenceGuide: "Reference guide",
  ohsClauses: "OH&S clauses",
  emsClauses: "EMS clauses",
  esgPrinciples: "ESG principles",
  cafOutcomes: "Objectives and outcomes",
} as const;

export function StandardHeader({ frameworkId, page, title, subtitle, aside, children }: {
  /** Sets the swatch and, unless `title` is given, the heading. */
  frameworkId?: string;
  /** The sub-page, as the sidebar names it. */
  page?: string;
  title?: string;
  subtitle?: React.ReactNode;
  aside?: React.ReactNode;
  /** Anything that belongs under the subtitle (a second line of scope, a link). */
  children?: React.ReactNode;
}) {
  const heading = title ?? (frameworkId ? STANDARD_TITLES[frameworkId] ?? frameworkId : "");
  return (
    <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
      <div style={{ minWidth: 0, flex: "1 1 420px" }}>
        {page && (
          <div style={{ ...text.eyebrow, display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            {frameworkId && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: frameworkColor(frameworkId), flexShrink: 0 }} />}
            {page}
          </div>
        )}
        <h1 style={pageHeading}>{heading}</h1>
        {subtitle && <p style={{ ...pageSubtitle, maxWidth: 820 }}>{subtitle}</p>}
        {children}
      </div>
      {aside && <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>{aside}</div>}
    </header>
  );
}

/** A short guided-mode explanation above a list; one style on every standard page. */
export function GuidedIntro({ title, children, icon }: { title: string; children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div style={{ background: theme.color.accentBg, border: `1px solid ${theme.color.accent}`, borderRadius: theme.radius.md, padding: "12px 16px", marginBottom: 16, display: "flex", gap: 12, alignItems: "flex-start" }}>
      {icon && <span style={{ flexShrink: 0, color: theme.color.accentText, display: "flex" }}>{icon}</span>}
      <div>
        <div style={{ ...text.body, fontWeight: 700, color: theme.color.accentText, marginBottom: 3 }}>{title}</div>
        <div style={{ ...text.hint, color: theme.color.muted }}>{children}</div>
      </div>
    </div>
  );
}

/** A reference note at the foot of a standard page (certification route, method). */
export function StandardNote({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ ...card, marginTop: 24, padding: "14px 18px" }}>
      <h2 style={{ ...text.sectionTitle, fontSize: text.body.fontSize }}>{title}</h2>
      <div style={{ ...text.hint }}>{children}</div>
    </section>
  );
}
